import {
  Accordion,
  Alert,
  Anchor,
  Badge,
  Button,
  Flex,
  Group,
  Loader,
  Progress,
  SegmentedControl,
  Select,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconSparkles } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ApiError, unwrap } from '../../api/client';
import {
  childCurriculum,
  getCurriculum,
  markTopic,
  type CurriculumSubject,
  type TopicStanding,
} from '../../api/gen';
import { SCHOOLS, schoolLabel, type School } from '../../lib/school';
import { useOrg, useSession } from '../../lib/session';

// Lehrplan.
//
// LehrplanPLUS for the child's school type and grade, subject by subject, and
// beside every topic where the child stands as the parent sees it: offen,
// nacharbeiten, sitzt. It opens on what the child's profile says; the parent
// can look at any other school type and grade there is a plan for.
// The mark is the parent's own; the sheets written on a topic are shown next
// to it as evidence, not as a verdict. From any topic a parent can have a
// sheet written — one step easier when the topic needs reworking.

type Status = 'open' | 'practice' | 'solid';

const STATUS: { value: Status; label: string; color: string }[] = [
  { value: 'open', label: 'Offen', color: 'gray.4' },
  { value: 'practice', label: 'Nacharbeiten', color: 'gray.6' },
  { value: 'solid', label: 'Sitzt', color: 'ink' },
];

export function CurriculumPage() {
  const org = useOrg();
  const { pairing } = useSession();
  const children = pairing?.children ?? [];
  const [childId, setChildId] = useState<string | null>(children[0]?.id ?? null);
  const startAt = (id: string | null) => {
    const c = children.find((x) => x.id === id);
    return { school: (c?.school ?? 'grundschule') as School, grade: c?.grade ?? 3 };
  };
  const [school, setSchool] = useState<School>(() => startAt(childId).school);
  const [grade, setGrade] = useState<number>(() => startAt(childId).grade);
  const pickChild = (id: string) => {
    setChildId(id);
    const at = startAt(id);
    setSchool(at.school);
    setGrade(at.grade);
  };

  const { data: curriculum, isLoading, error } = useQuery({
    queryKey: ['curriculum', school],
    queryFn: () => unwrap(getCurriculum({ headers: org, query: { school } })),
    staleTime: Infinity,
    retry: false,
  });

  const { data: standing } = useQuery({
    queryKey: ['standing', childId],
    enabled: !!childId,
    queryFn: () => unwrap(childCurriculum({ headers: org, path: { childId: childId! } })),
  });

  const schoolPicker = (
    <Select
      w={170}
      aria-label="Schulform"
      data={SCHOOLS.map((x) => ({ value: x.value, label: x.label }))}
      value={school}
      allowDeselect={false}
      onChange={(v) => v && setSchool(v as School)}
    />
  );

  if (isLoading) return <Loader />;
  if (!curriculum) {
    return (
      <Stack>
        <Title order={3}>Lehrplan</Title>
        <Group>{schoolPicker}</Group>
        <Alert color={error instanceof ApiError && error.status === 404 ? 'blue' : 'red'}>
          {error instanceof ApiError && error.status === 404
            ? `Für die ${schoolLabel(school)} gibt es den Lehrplan hier noch nicht.`
            : 'Der Lehrplan lässt sich gerade nicht laden.'}
        </Alert>
      </Stack>
    );
  }

  const byRef = new Map<string, TopicStanding>((standing?.topics ?? []).map((t) => [t.topic, t]));
  const grades = [...new Set(curriculum.subjects.flatMap((s) => s.grades.map((g) => g.grade)))].sort((a, b) => a - b);
  const shownGrade = grades.includes(grade) ? grade : grades[0];

  return (
    <Stack>
      <Stack gap={4}>
        <Title order={3}>Lehrplan</Title>
        <Text c="dimmed" size="sm">
          Was im {curriculum.name} in Klasse {shownGrade} dran ist, Fach für Fach. Markieren Sie,
          was bei Ihrem Kind sitzt und was noch nachgearbeitet werden kann. Zu jedem Thema schreibt
          Sokrates auf Wunsch ein passendes Arbeitsblatt.
        </Text>
        <Text c="dimmed" size="xs">
          {curriculum.school === 'grundschule'
            ? 'Bayern hat einen gemeinsamen Plan für 3 und 4. Die Aufteilung auf die beiden Klassen folgt der üblichen Reihenfolge der Schulbücher und ist unsere. '
            : ''}
          <Anchor href={curriculum.source} target="_blank" size="xs">
            LehrplanPLUS öffnen
          </Anchor>
        </Text>
      </Stack>

      <Group>
        {children.length > 0 ? (
          <SegmentedControl
            value={childId ?? ''}
            onChange={pickChild}
            data={children.map((c) => ({ value: c.id, label: c.name }))}
          />
        ) : null}
        {schoolPicker}
        <SegmentedControl
          value={String(shownGrade)}
          onChange={(v) => setGrade(Number(v))}
          data={grades.map((g) => ({ value: String(g), label: `Klasse ${g}` }))}
        />
      </Group>

      {children.length === 0 ? (
        <Alert variant="light">Legen Sie zuerst ein Kind an, um seinen Stand zu markieren.</Alert>
      ) : null}

      <Accordion variant="separated" multiple defaultValue={['deutsch', 'mathematik']}>
        {curriculum.subjects
          .filter((subject) => subject.grades.some((g) => g.grade === shownGrade))
          .map((subject) => (
            <SubjectPanel
              key={subject.id}
              school={curriculum.school}
              subject={subject}
              grade={shownGrade}
              childId={childId}
              byRef={byRef}
            />
          ))}
      </Accordion>
    </Stack>
  );
}

function SubjectPanel({
  school,
  subject,
  grade,
  childId,
  byRef,
}: {
  school: string;
  subject: CurriculumSubject;
  grade: number;
  childId: string | null;
  byRef: Map<string, TopicStanding>;
}) {
  const g = subject.grades.find((x) => x.grade === grade);
  const refs = (g?.areas ?? []).flatMap((a) =>
    a.topics.map((t) => `${school}/${subject.id}/${grade}/${a.id}/${t.id}`),
  );
  const count = (status: Status) =>
    refs.filter((r) => (byRef.get(r)?.status ?? 'open') === status).length;
  const solid = count('solid');
  const practice = count('practice');

  return (
    <Accordion.Item value={subject.id}>
      <Accordion.Control>
        <Group justify="space-between" wrap="nowrap" pr="sm">
          <Group gap="xs">
            <Text fw={600}>{subject.name}</Text>
            {!subject.worksheets ? (
              <Badge size="xs" variant="light" color="gray">
                ohne Arbeitsblätter
              </Badge>
            ) : null}
          </Group>
          {refs.length > 0 ? (
            <Group gap="xs" wrap="nowrap" w={{ base: 150, sm: 260 }}>
              <Progress.Root size="md" style={{ flex: 1 }}>
                <Progress.Section value={(solid / refs.length) * 100} color="ink" />
                <Progress.Section value={(practice / refs.length) * 100} color="gray.4" />
              </Progress.Root>
              <Text size="xs" c="dimmed" w={{ base: 50, sm: 110 }}>
                {solid}/{refs.length}
                <Text span size="xs" c="dimmed" visibleFrom="sm">
                  {' '}sitzen
                </Text>
              </Text>
            </Group>
          ) : null}
        </Group>
      </Accordion.Control>
      <Accordion.Panel>
        <Stack gap="lg">
          {(g?.areas ?? []).map((area) => (
            <Stack key={area.id} gap="xs">
              <Text fw={600} size="sm">
                {area.name}
              </Text>
              {area.topics.length === 0 ? (
                <Text size="xs" c="dimmed">
                  Ohne einzelne Themen im Plan.
                </Text>
              ) : (
                area.topics.map((topic) => {
                  const ref = `${school}/${subject.id}/${grade}/${area.id}/${topic.id}`;
                  return (
                    <TopicRow
                      key={ref}
                      topicRef={ref}
                      name={topic.name}
                      goals={topic.goals}
                      parentsOnly={!!topic.parentsOnly}
                      standing={byRef.get(ref)}
                      childId={childId}
                      canWrite={subject.worksheets}
                    />
                  );
                })
              )}
            </Stack>
          ))}
          {subject.source ? (
            <Anchor href={subject.source} target="_blank" size="xs">
              Fachlehrplan {subject.name}
            </Anchor>
          ) : null}
        </Stack>
      </Accordion.Panel>
    </Accordion.Item>
  );
}

function TopicRow({
  topicRef,
  name,
  goals,
  parentsOnly,
  standing,
  childId,
  canWrite,
}: {
  topicRef: string;
  name: string;
  goals: string[];
  parentsOnly: boolean;
  standing?: TopicStanding;
  childId: string | null;
  canWrite: boolean;
}) {
  const org = useOrg();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const status = (standing?.status ?? 'open') as Status;

  const mark = useMutation({
    mutationFn: (next: Status) =>
      unwrap(markTopic({ headers: org, path: { childId: childId! }, body: { topic: topicRef, status: next } })),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['standing', childId] }),
    onError: (err) => notifications.show({ color: 'red', message: (err as Error).message }),
  });

  const write = () => {
    const params = new URLSearchParams({ topic: topicRef });
    if (childId) params.set('child', childId);
    // A topic that needs reworking starts a step easier.
    if (status === 'practice') params.set('level', '-1');
    navigate(`/eltern/arbeitsblaetter?${params}`);
  };

  // Side by side on a wide screen; on a phone the controls go under the
  // topic, or the name is squeezed to one letter per line.
  return (
    <Flex
      direction={{ base: 'column', sm: 'row' }}
      justify="space-between"
      align={{ base: 'stretch', sm: 'flex-start' }}
      gap="xs"
      p="sm"
      style={{ borderRadius: 'var(--mantine-radius-md)', border: '1px solid var(--mantine-color-default-border)' }}
    >
      <Stack gap={2} style={{ minWidth: 0, flex: 1 }}>
        <Group gap="xs">
          <Text size="sm" fw={500}>
            {name}
          </Text>
          {parentsOnly ? (
            <Badge size="xs" variant="light" color="grape">
              Blätter nur für Eltern
            </Badge>
          ) : null}
        </Group>
        {goals.length > 0 ? (
          <Text size="xs" c="dimmed">
            {goals.join(' · ')}
          </Text>
        ) : null}
        {standing && standing.sheets > 0 ? (
          <Text size="xs" c="dimmed">
            {standing.sheets} Blatt{standing.sheets === 1 ? '' : 'er'} geschrieben
          </Text>
        ) : null}
      </Stack>
      <Group gap="xs" wrap="wrap" style={{ flexShrink: 0 }}>
        {childId ? (
          <SegmentedControl
            size="xs"
            value={status}
            disabled={mark.isPending}
            onChange={(v) => mark.mutate(v as Status)}
            color={STATUS.find((s) => s.value === status)?.color}
            data={STATUS.map((s) => ({ value: s.value, label: s.label }))}
          />
        ) : null}
        {canWrite ? (
          <Button size="compact-xs" variant="light" leftSection={<IconSparkles size={14} />} onClick={write}>
            Blatt dazu
          </Button>
        ) : null}
      </Group>
    </Flex>
  );
}
